import _ from "lodash";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { TAB_TYPES } from "../context/ParticipantsModalContext";
import {
  getParticipantStudentsQuery,
  getParticipantStaffQuery,
  getParticipantFamilyMembersQuery,
} from "../modules/ParticipantsQueries";
import { SEARCH_DEBOUNCE_MS } from "../modules/ParticipantsConstants";

const PAGE_SIZE = 20;

/**
 * Reusable hook to fetch paginated participants (students, staff, or family members)
 * for the active tab, with server-side search and cursor-based pagination.
 *
 * Only the query for the currently active tab fires — the others are skipped.
 *
 * @param {{ organizationId: string, activeTab: string, searchText: string, gradeIds: string[], courseIds: string[] }} params
 * @returns {{ participants: object[], totalCount: number, isLoading: boolean, isFetchingMore: boolean, hasNextPage: boolean, loadMore: Function }}
 */
const useParticipantsFeed = ({
  organizationId,
  activeTab,
  searchText,
  gradeIds = [],
  courseIds = [],
  skip = false,
}) => {
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchText]);

  const isStudentTab = activeTab === TAB_TYPES.STUDENTS;
  const isStaffTab = activeTab === TAB_TYPES.STAFF;
  const isFamilyTab = activeTab === TAB_TYPES.FAMILY;

  const studentVariables = useMemo(
    () => ({
      organizationId,
      first: PAGE_SIZE,
      filters: {
        searchText: debouncedSearchText,
        grades: gradeIds,
        courses: courseIds,
      },
    }),
    [organizationId, debouncedSearchText, gradeIds, courseIds]
  );

  const staffVariables = useMemo(
    () => ({
      organizationId,
      first: PAGE_SIZE,
      filters: {
        searchText: debouncedSearchText,
        grades: gradeIds,
        courses: courseIds,
      },
    }),
    [organizationId, debouncedSearchText, gradeIds, courseIds]
  );

  const familyVariables = useMemo(
    () => ({
      organizationId,
      first: PAGE_SIZE,
      familyMembersFilter: {
        searchText: debouncedSearchText,
        grades: gradeIds,
        courses: courseIds,
      },
    }),
    [organizationId, debouncedSearchText, gradeIds, courseIds]
  );

  const {
    data: studentData,
    networkStatus: studentNetworkStatus,
    fetchMore: fetchMoreStudents,
  } = useQuery(getParticipantStudentsQuery, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: studentVariables,
    skip: skip || !isStudentTab || !organizationId,
  });

  const {
    data: staffData,
    networkStatus: staffNetworkStatus,
    fetchMore: fetchMoreStaff,
  } = useQuery(getParticipantStaffQuery, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: staffVariables,
    skip: skip || !isStaffTab || !organizationId,
  });

  const {
    data: familyData,
    networkStatus: familyNetworkStatus,
    fetchMore: fetchMoreFamily,
  } = useQuery(getParticipantFamilyMembersQuery, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: familyVariables,
    skip: skip || !isFamilyTab || !organizationId,
  });

  const activeTabConfig = useMemo(() => {
    if (isStudentTab) {
      return {
        connection: _.get(studentData, "node.students"),
        networkStatus: studentNetworkStatus,
        fetchMore: fetchMoreStudents,
        connectionKey: "students",
        variables: studentVariables,
        isLoading:
          _.includes([1, 2], studentNetworkStatus) && _.isEmpty(studentData),
      };
    }
    if (isStaffTab) {
      return {
        connection: _.get(staffData, "node.staff"),
        networkStatus: staffNetworkStatus,
        fetchMore: fetchMoreStaff,
        connectionKey: "staff",
        variables: staffVariables,
        isLoading:
          _.includes([1, 2], staffNetworkStatus) && _.isEmpty(staffData),
      };
    }
    if (isFamilyTab) {
      return {
        connection: _.get(familyData, "node.familyMembers"),
        networkStatus: familyNetworkStatus,
        fetchMore: fetchMoreFamily,
        connectionKey: "familyMembers",
        variables: familyVariables,
        isLoading:
          _.includes([1, 2], familyNetworkStatus) && _.isEmpty(familyData),
      };
    }
    return {
      connection: null,
      networkStatus: 7,
      fetchMore: null,
      connectionKey: null,
      variables: {},
      isLoading: false,
    };
  }, [
    isStudentTab,
    isStaffTab,
    isFamilyTab,
    studentData,
    staffData,
    familyData,
    studentNetworkStatus,
    staffNetworkStatus,
    familyNetworkStatus,
    fetchMoreStudents,
    fetchMoreStaff,
    fetchMoreFamily,
    studentVariables,
    staffVariables,
    familyVariables,
  ]);

  const {
    connection,
    networkStatus,
    fetchMore,
    connectionKey,
    variables,
    isLoading,
  } = activeTabConfig;

  const participants = _.map(_.get(connection, "edges", []), "node");
  const totalCount = _.get(connection, "totalCount", 0);
  const hasNextPage = _.get(connection, "pageInfo.hasNextPage", false);
  const endCursor = _.get(connection, "pageInfo.endCursor", null);

  const isFetchingMore = networkStatus === 3;

  const loadMore = useCallback(() => {
    if (!hasNextPage || !fetchMore || isFetchingMore) return;

    fetchMore({
      variables: { ...variables, after: endCursor },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const prevConnection = _.get(
          previousResult,
          `node.${connectionKey}`,
          {}
        );
        const nextConnection = _.get(
          fetchMoreResult,
          `node.${connectionKey}`,
          {}
        );
        const prevEdges = _.get(prevConnection, "edges", []);
        const nextEdges = _.get(nextConnection, "edges", []);
        const nextPageInfo = _.get(nextConnection, "pageInfo", {});

        return {
          ...previousResult,
          node: {
            ...previousResult.node,
            [connectionKey]: {
              ...prevConnection,
              edges: _.concat(prevEdges, nextEdges),
              pageInfo: nextPageInfo,
            },
          },
        };
      },
    });
  }, [
    hasNextPage,
    fetchMore,
    isFetchingMore,
    variables,
    endCursor,
    connectionKey,
  ]);

  return {
    participants,
    totalCount,
    isLoading,
    isFetchingMore,
    hasNextPage,
    loadMore,
  };
};

export default useParticipantsFeed;
